// src/components/buyer-leads/ConvertToDealModal.jsx
import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { convertBuyerLeadToDeal } from "../../utils/firebasefunctions";
import { notifyDealMilestone } from "../../utils/helper";
import { resolveSystemSource } from "../../utils/leadSourceLabel";
import { actionType } from "../../context/reducer";

export default function ConvertToDealModal({ lead, onClose, onConverted }) {
  const [{ user, deals, users, leadsource }, dispatch] = useStateValue();
  const [propertyaddress, setPropertyaddress] = useState(lead.propertyaddress || "");
  const [offerPrice, setOfferPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!propertyaddress.trim()) { toast.error("Property address is required"); return; }
    setBusy(true);
    const res = await convertBuyerLeadToDeal(
      lead,
      {
        propertyaddress: propertyaddress.trim(),
        offerPrice: offerPrice.trim(),
        // Deals store the full system lead-source object; resolve the lead's
        // stored id (or legacy text) so commission tracking keeps working.
        leadsource:
          resolveSystemSource(lead.leadSource, leadsource) || lead.leadSource || "",
      },
      user,
    );
    setBusy(false);
    if (res.success) {
      notifyDealMilestone(
        {
          ...res.deal,
          agentData: res.deal?.agentData || lead?.assignedAgent,
          propertyaddress: res.deal?.propertyaddress || propertyaddress.trim(),
          // Migrated leads return only { dealId } (no `deal`), so anchor the
          // notification on the real deal id either way.
          createdat: res.deal?.createdat || res.dealId,
        },
        "Under Contract",
        users,
        user,
      ).catch(() => {});
      dispatch({ type: actionType.STORE_DEALS, deals: [...(deals || []), res.deal] });
      toast.success("Deal created — review and approve it in Pending Approval");
      onConverted?.();
      onClose();
    } else {
      toast.error(res.error || "Conversion failed");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Convert to Deal</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={18} /></button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="text-sm text-slate-600">
              Buyer: <span className="font-semibold text-slate-800">{lead.contactName}</span>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Property Address</label>
              <input type="text" value={propertyaddress} onChange={(e) => setPropertyaddress(e.target.value)}
                className="w-full text-sm rounded-lg border border-slate-300 focus:border-[#1E293B] outline-none px-3 py-2"
                placeholder="123 Maple Dr" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Accepted Price</label>
              <input type="number" value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)}
                className="w-full text-sm rounded-lg border border-slate-300 focus:border-[#1E293B] outline-none px-3 py-2"
                placeholder="510000" />
            </div>
          </div>
          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy}
              className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-60">
              {busy ? "Creating…" : "Create Deal →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
